import { useLayoutEffect, useState } from 'react';

import { getLocalStorageItem } from '@revenuemarket/browser';
import {
  Button,
  ClarifyLine,
  PaymentsLine,
  BellLine,
  QuestionCircleLine,
  DatabaseLine,
  DotLine,
  Colors,
  KeyboardTabLine,
  CloseLine,
  CelebrationLine,
  HomeLine,
  Flex,
  Text,
  LinkLine,
} from '@revenuemarket/cds';
import { CUSTOM_ATTRIBUTES } from '@revenuemarket/utils';
import { Divider } from 'antd';
import dayjs from 'dayjs';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { match } from 'ts-pattern';

import { TermOffered } from '~apis/dashboard';
import { CompanyUserPermission } from '~apis/role/type';
import AnalysisCreateButton from '~components/Analysis/AnalysisCreateButton';
import NavFinanceBanner from '~components/FinanceInfo/NavBanner';
import { PermissionGuard } from '~components/Role/PermissionGuard';
import BetaBadge from '~components/ui/BetaBadge';
import { DEMO_TRIAL_TOOLTIP_MSG } from '~constants/clobe';
import checkIsOnboardingUser from '~hooks/checkIsOnboardingUser';
import { useGetAnalysisList } from '~hooks/queries/analysis/query';
import { useCompany } from '~hooks/queries/companies/query';
import { useGetFinanceInfo } from '~hooks/queries/dashboard/query';
import { useUserPermissions } from '~hooks/queries/role/query';
import { useGetOnboardingStatus, useGetSettings } from '~hooks/queries/setting/query';
import { useCompanyId } from '~hooks/useCompanyId';
import { useDemoCompanyId } from '~hooks/useDemoCompanyId';
import useIsDemoTrial from '~hooks/useIsDemoTrial';
import useWiringAvailable from '~hooks/useWiringAvailable';
import { css } from '~libs/emotion';

import ClobeStartInfo from './ClobeStartInfo';
import NavGroup from './NavGroup';
import NavigationMenu from './NavigationMenu';
import NavigationMenuGroup from './NavigationMenuGroup';
import ScrapeSection from './ScrapeSection';
import { ANALYSIS_NAV_ITEMS, DATA_NAV_GROUP_ITEMS, LABELING_NAV_GROUP_ITEMS } from './constant';
import { useFinanceMenu } from './useFinanceMenu';
import CompanyInfoDropDown from '../CompanyInfoDropDown';

interface Props {
  onPannelPinClick: () => void;
  onCloseClick?: () => void;
  floating?: boolean;
  isPartnerIntro?: boolean;
}

const NavigationBody: React.FC<Props> = ({
  onPannelPinClick,
  floating,
  onCloseClick,
  isPartnerIntro = false,
}) => {
  const { companyId } = useCompanyId();
  const demoCompanyId = useDemoCompanyId();
  const isDemoTrial = useIsDemoTrial();
  const { data: onboardingStatus } = useGetOnboardingStatus();
  const { data: userPermissionsData } = useUserPermissions();
  const userPermissions = userPermissionsData?.Data?.data?.companyUserPermissions ?? [];
  const onboardingStatusData = onboardingStatus?.Data?.data;
  const allFinished =
    onboardingStatusData?.bankAccountRegistered &&
    onboardingStatusData?.ntsCertificateRegistered &&
    onboardingStatusData?.hasInvitation;

  const [showStartInfo, setShowStartInfo] = useState(false);

  useLayoutEffect(() => {
    const hide = getLocalStorageItem(`${companyId}_clobe_start_info_hide`);

    if (hide) {
      setShowStartInfo(false);
    } else {
      setShowStartInfo(true);
    }
  }, [companyId, allFinished]);

  const company = useCompany({ companyId });

  const { data } = useGetFinanceInfo({
    companyId,
    checkPermission: true,
    permissionEnabled: userPermissions.includes(CompanyUserPermission.CAN_MANAGE_CLOBE_FINANCE),
  });

  const termOffered = data?.Data?.data?.items.find(
    (item) => item?.type === 'TERM_OFFERED',
  ) as TermOffered;

  const showBanner = termOffered?.finalOffer === 'OFFERED';

  const analysis = useGetAnalysisList({
    companyId: demoCompanyId ?? companyId,
    enabled: !!userPermissions?.includes(CompanyUserPermission.CAN_MANAGE_ANALYSIS),
  });

  const analysisList = analysis.data?.Data?.data?.items || [];

  const settings = useGetSettings();
  const settingData = settings?.data?.Data?.data;
  const step = settingData?.onboardingStep;
  const isOnboardingUser = checkIsOnboardingUser(step);
  const wiringAvailable = useWiringAvailable();

  const financeMenu = useFinanceMenu();
  const navForOnboardingUser = isOnboardingUser && !demoCompanyId;
  const isHiddenCardSalesMenu =
    company.data?.Data?.data?.createdAt &&
    dayjs(company.data?.Data?.data?.createdAt).isAfter(dayjs('2025-12-01'));

  return (
    <Flex
      direction="column"
      justify="space-between"
      style={{
        height: '100%',
        minHeight: 0, // 추가: flex item이 content에 의해 늘어나는 것을 방지
        padding: '12px 0',
      }}
      className={css`
        *::-webkit-scrollbar-track {
          background-color: transparent;
        }
      `}
    >
      <Flex
        direction="column"
        style={{
          flex: 1,
          minHeight: 0, // 추가: flex shrinking 허용
          overflow: 'hidden', // 추가: 전체 영역 넘침 방지
        }}
      >
        <Flex
          justify="space-between"
          style={{
            marginBottom: 8,
            flexShrink: 0, // 추가: 헤더 영역이 줄어들지 않도록
            padding: '0 12px',
          }}
          gap={4}
        >
          <CompanyInfoDropDown />
          <Button
            icon={
              floating ? (
                <CloseLine />
              ) : (
                <KeyboardTabLine
                  className={css`
                    transform: rotate(180deg);
                  `}
                />
              )
            }
            buttonType="transparent"
            style={{
              width: 32,
            }}
            onClick={() => {
              if (floating) {
                onCloseClick?.();
                return;
              }
              onPannelPinClick();
            }}
            {...CUSTOM_ATTRIBUTES.NAVIGATION_BODY.PIN_UNPIN_BUTTON(floating)}
          />
        </Flex>
        <div style={{ flexShrink: 0, padding: '0 12px' }}>
          {' '}
          {/* 추가: 고정 영역들을 flexShrink: 0으로 래핑 */}
          <ScrapeSection />
          <PermissionGuard required={CompanyUserPermission.CAN_MANAGE_CLOBE_FINANCE}>
            {showBanner ? (
              <NavFinanceBanner companyName={company.data?.Data?.data?.name || ''} />
            ) : null}
          </PermissionGuard>
          {showStartInfo && <ClobeStartInfo setShowStartInfo={setShowStartInfo} />}
        </div>

        <Flex
          direction="column"
          style={{
            flex: 1,
            minHeight: 0, // 추가: 중요! 이것이 스크롤의 핵심
            overflow: 'hidden', // 추가: 이 영역에서의 넘침 방지
          }}
        >
          <Flex
            direction="column"
            style={{
              flexShrink: 0, // 추가: 홈 메뉴가 줄어들지 않도록
              padding: '8px 12px 16px 12px',
            }}
          >
            <NavigationMenu
              activeKey="home"
              title="홈"
              icon={<HomeLine />}
              link="/clobe/home"
              customAttributes={CUSTOM_ATTRIBUTES.NAVIGATION_BODY.HOME_MENU}
            />
            {companyId && !isDemoTrial && !isPartnerIntro ? (
              <NavigationMenu
                activeKey=""
                title="최대 1000만원 추천 이벤트"
                icon={<CelebrationLine />}
                link="/clobe/referral-event"
                customAttributes={CUSTOM_ATTRIBUTES.REFERRAL_EVENT}
              />
            ) : null}
          </Flex>
          <OverlayScrollbarsComponent
            style={{
              flex: 1,
              minHeight: 0, // 중요: 이 설정이 스크롤을 가능하게 함
              overflowY: 'auto', // scroll -> auto로 변경 (필요할 때만 스크롤바 표시)
            }}
            data-description="Scrollable navigation menu sections including data, analysis, and finance features"
          >
            <div style={{ padding: '0 12px' }}>
              <NavGroup
                title="데이터"
                style={{
                  marginBottom: 16,
                }}
                data-description="Data management section for account information, transactions, and data labeling"
              >
                {DATA_NAV_GROUP_ITEMS.map((item) => {
                  if (isHiddenCardSalesMenu && item.activeKey === 'card-sales') {
                    return null;
                  }
                  if (
                    item.activeKey === 'transactions' ||
                    item.activeKey === 'card-approval' ||
                    item.activeKey === 'tax-invoice-list' ||
                    item.activeKey === 'cash-receipt' ||
                    item.activeKey === 'card-sales' ||
                    item.activeKey === 'pg-marketplace'
                  ) {
                    return (
                      <PermissionGuard
                        key={item.activeKey}
                        required={match(item.activeKey)
                          .with(
                            'transactions',
                            () => CompanyUserPermission.CAN_MANAGE_BANK_TRANSACTION,
                          )
                          .with(
                            'card-approval',
                            () => CompanyUserPermission.CAN_MANAGE_CARD_TRANSACTION,
                          )
                          .with(
                            'tax-invoice-list',
                            () => CompanyUserPermission.CAN_MANAGE_TAX_INVOICE,
                          )
                          .with('cash-receipt', () => CompanyUserPermission.CAN_MANAGE_CASH_RECEIPT)
                          .with('card-sales', () => CompanyUserPermission.CAN_MANAGE_CARD_SALES)
                          .with(
                            'pg-marketplace',
                            () =>
                              CompanyUserPermission.CAN_MANAGE_SETTLEMENT_ORGANIZATION_MONTHLY_SALES_VAT,
                          )
                          .exhaustive()}
                      >
                        <NavigationMenu
                          link={item.link}
                          key={item.activeKey}
                          activeKey={item.activeKey}
                          title={item.title}
                          icon={<DotLine />}
                          data-description="Core data features including account management, transaction history, and financial records"
                          rightSlot={
                            item.activeKey === 'pg-marketplace' ? (
                              <BetaBadge title="해당 메뉴는 베타 기능입니다. 에러 또는 개선 사항이 있으면 채팅으로 문의를 남겨주세요." />
                            ) : null
                          }
                        />
                      </PermissionGuard>
                    );
                  }
                  return (
                    <NavigationMenu
                      link={item.link}
                      key={item.activeKey}
                      activeKey={item.activeKey}
                      title={item.title}
                      icon={<DotLine />}
                      data-description="Core data features including account management, transaction history, and financial records"
                    />
                  );
                })}{' '}
              </NavGroup>
              <PermissionGuard
                required={[
                  CompanyUserPermission.CAN_MANAGE_LABELING_RULES,
                  CompanyUserPermission.CAN_MANAGE_BANK_TRANSACTIONS_LABELING,
                  CompanyUserPermission.CAN_MANAGE_CARD_BILLINGS_LABELING,
                  CompanyUserPermission.CAN_MANAGE_SALES_PURCHASES_SETTLEMENT,
                ]}
                mode="some"
              >
                <NavGroup
                  title="데이터 분류 및 정산"
                  style={{
                    marginBottom: 16,
                  }}
                >
                  {LABELING_NAV_GROUP_ITEMS.map((item) => {
                    if (
                      item.activeKey !== 'label-management' &&
                      item.activeKey !== 'card-transaction-labeling'
                    ) {
                      return (
                        <PermissionGuard
                          key={item.activeKey}
                          required={match(item.activeKey)
                            .with(
                              'bank-labeling',
                              () => CompanyUserPermission.CAN_MANAGE_BANK_TRANSACTIONS_LABELING,
                            )
                            .with(
                              'card-billing-labeling',
                              () => CompanyUserPermission.CAN_MANAGE_CARD_BILLINGS_LABELING,
                            )
                            .with(
                              'sales-settlement',
                              () => CompanyUserPermission.CAN_MANAGE_SALES_PURCHASES_SETTLEMENT,
                            )
                            .with(
                              'purchase-settlement',
                              () => CompanyUserPermission.CAN_MANAGE_SALES_PURCHASES_SETTLEMENT,
                            )
                            .otherwise(
                              () => CompanyUserPermission.CAN_MANAGE_BANK_TRANSACTIONS_LABELING,
                            )}
                        >
                          <NavigationMenu
                            link={item.link}
                            key={item.activeKey}
                            activeKey={item.activeKey}
                            title={item.title}
                            icon={<DotLine />}
                          />
                        </PermissionGuard>
                      );
                    }
                    return (
                      <NavigationMenu
                        link={item.link}
                        key={item.activeKey}
                        activeKey={item.activeKey}
                        title={item.title}
                        icon={<DotLine />}
                      />
                    );
                  })}
                </NavGroup>
              </PermissionGuard>

              <PermissionGuard
                required={[
                  CompanyUserPermission.CAN_MANAGE_ANALYSIS,
                  CompanyUserPermission.CAN_MANAGE_ACCOUNTS,
                  CompanyUserPermission.CAN_MANAGE_BUSINESS_ENTITIES,
                ]}
                mode="some"
              >
                <NavGroup
                  title="분석"
                  style={{
                    marginBottom: 16,
                  }}
                  data-description="Analysis section for financial analysis, profit/loss reports, and custom analytics"
                >
                  {ANALYSIS_NAV_ITEMS.map((item) => {
                    return (
                      <PermissionGuard
                        required={match(item.activeKey)
                          .with('cash-status', () => CompanyUserPermission.CAN_MANAGE_ACCOUNTS)
                          .with('cash-flow', () => CompanyUserPermission.CAN_MANAGE_ANALYSIS)
                          .with(
                            'sales-purchase',
                            () => CompanyUserPermission.CAN_MANAGE_BUSINESS_ENTITIES,
                          )
                          .otherwise(() => CompanyUserPermission.CAN_MANAGE_ANALYSIS)}
                        key={item.activeKey}
                      >
                        <NavigationMenu
                          link={item.link}
                          key={item.activeKey}
                          activeKey={item.activeKey}
                          title={item.title}
                          icon={item.icon}
                          data-description={`Access ${item.title} analysis features and reports`}
                        />
                      </PermissionGuard>
                    );
                  })}
                  {navForOnboardingUser ? (
                    <div />
                  ) : (
                    <PermissionGuard required={CompanyUserPermission.CAN_MANAGE_ANALYSIS}>
                      <NavigationMenuGroup
                        title="맞춤 분석"
                        titleNode={
                          <Flex
                            align="center"
                            justify="space-between"
                            style={{
                              width: '100%',
                            }}
                          >
                            <Text
                              textStyle="14_400"
                              style={{
                                color: Colors.gray700,
                              }}
                            >
                              맞춤 분석
                            </Text>
                            <AnalysisCreateButton />
                          </Flex>
                        }
                        items={[
                          ...analysisList.map((item) => {
                            return {
                              title: item.name,
                              link: `/clobe/analysis/${item.id}`,
                              activeKey: `analysis-${item.id}`,
                              dataType: 'analysis',
                            };
                          }),
                        ]}
                        data-description="Custom analysis reports created by user for specific business insights"
                      />
                    </PermissionGuard>
                  )}
                </NavGroup>
              </PermissionGuard>
              <PermissionGuard required={CompanyUserPermission.CAN_MANAGE_CLOBE_FINANCE}>
                <NavGroup
                  title="금융"
                  style={{
                    marginBottom: 16,
                  }}
                  data-description="Finance section for financial services, lending, and banking features"
                >
                  {financeMenu.menuItems.map((item) => {
                    return (
                      <NavigationMenu
                        link={item.link}
                        key={item.activeKey}
                        activeKey={item.activeKey}
                        title={item.title}
                        icon={<DotLine />}
                        data-description={`Access ${item.title} financial services and features`}
                      />
                    );
                  })}
                </NavGroup>
              </PermissionGuard>
            </div>
          </OverlayScrollbarsComponent>
        </Flex>
      </Flex>
      <Flex
        direction="column"
        data-description="Navigation footer with essential business tools and support options"
        style={{
          padding: '0 12px',
        }}
      >
        <Divider
          type="horizontal"
          style={{
            width: '100%',
            marginBottom: 8,
            marginTop: 8,
          }}
        />
        <Flex direction="column">
          <PermissionGuard required={CompanyUserPermission.CAN_MANAGE_TAX_INVOICE_ISSUANCE}>
            <NavigationMenu
              activeKey="tax-invoice-issue-list"
              link="/clobe/tax-invoice?type=issue"
              title="세금계산서 발급"
              disabled={isDemoTrial}
              tooltipMsg={isDemoTrial ? DEMO_TRIAL_TOOLTIP_MSG : undefined}
              icon={<ClarifyLine />}
              data-description="Issue tax invoices for business transactions and sales"
            />
          </PermissionGuard>
          <PermissionGuard required={CompanyUserPermission.CAN_MANAGE_WIRING}>
            {wiringAvailable ? (
              <NavigationMenu
                disabled={isDemoTrial}
                tooltipMsg={isDemoTrial ? DEMO_TRIAL_TOOLTIP_MSG : undefined}
                activeKey="wiring-list"
                title="이체"
                link="/clobe/wiring-list"
                icon={<PaymentsLine />}
                data-description="Manage wire transfers and payment transactions"
              />
            ) : null}
          </PermissionGuard>

          <PermissionGuard required={CompanyUserPermission.CAN_MANAGE_NOTIFICATIONS}>
            <NavigationMenu
              disabled={isDemoTrial}
              tooltipMsg={isDemoTrial ? DEMO_TRIAL_TOOLTIP_MSG : undefined}
              activeKey="notifications"
              title="알림"
              link="/clobe/notifications"
              icon={<BellLine />}
              data-description="View system notifications, alerts, and important updates"
            />
          </PermissionGuard>

          <PermissionGuard required={CompanyUserPermission.CAN_MANAGE_DATA_INTEGRATION}>
            <NavigationMenu
              disabled={isDemoTrial}
              tooltipMsg={isDemoTrial ? DEMO_TRIAL_TOOLTIP_MSG : undefined}
              activeKey="integration"
              title="연동"
              link="/clobe/data-manage"
              icon={<DatabaseLine />}
              data-description="Manage data connections and integrations with banks and financial institutions"
            />
          </PermissionGuard>

          <NavigationMenu
            activeKey="guide"
            title="이용 가이드"
            icon={<LinkLine />}
            link="https://docs.clobe.ai/"
            data-description="Access user guide and documentation"
          />

          {!isPartnerIntro && (
            <NavigationMenu
              disabled={isDemoTrial}
              tooltipMsg={isDemoTrial ? DEMO_TRIAL_TOOLTIP_MSG : undefined}
              activeKey="question"
              title="문의하기"
              icon={<QuestionCircleLine />}
              data-description="Access customer support, help documentation, and submit inquiries"
              onClick={() => {
                window.Intercom('showSpace', 'home');
              }}
            />
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};

export default NavigationBody;
